import React, { useCallback, useEffect, useState } from 'react';

import 'wicket';
import 'wicket/wicket-gmap3';
import { App } from '@flipdish/api-client-typescript';
import makeStyles from '@mui/styles/makeStyles';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { connect } from 'react-redux';

import { deliveryZonesConstants } from '../../constants/deliveryzones.constants';
import { GOOGLE_API_KEY } from '../../constants/googleApiKey.constant';
import { libraries } from '../../constants/map.constant';
import { createLoadingSelector } from '../../selectors/loading.selector';
import {
  createDisplayDeliveryZoneIdsSelector,
  createDisplayDeliveryZonesSelector,
} from '../../selectors/storeDeliveryZone.selector';
import MapItem from './components/MapItem';
import { IDeliveryZone } from './types';

export interface MapProps {
  storeId: number;
  mapCenter: google.maps.LatLng | google.maps.LatLngLiteral;
  isSmallScreen: boolean;
  onChange: (changes: IDeliveryZone) => void;
  onSelect: (deliveryZoneId: number) => void;
}
type Props = MapProps & MappedState;

const useStyles = makeStyles({
  mapWrapper: {
    height: '100%',
  },
  hideCreatePoint: {
    // Google Maps does not expose an option to disable polygon midpoint handles.
    // Use this workaround when the polygon reaches the maximum point count to hide
    // the semi-transparent midpoint edit handles that insert new vertices, while
    // leaving the existing vertex handles available for editing.
    '& .gm-style div[style*="cursor: pointer"]:has(> div[style*="opacity: 0.5"])': {
      display: 'none !important',
    },
  },
});

const GMap = ({
  storeId: _storeId,
  mapCenter,
  zoneIds,
  displayZones,
  onChange,
  onSelect,
  isSmallScreen,
  canEdit,
  createEventFired,
  selectedId,
}: Props) => {
  const classes = useStyles();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_API_KEY ?? '',
    libraries,
  });

  const onMapLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onMapUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isPointCapActive, setIsPointCapActive] = useState(false);

  useEffect(() => {
    map?.panTo(mapCenter);
  }, [createEventFired]);

  useEffect(() => {
    if (!canEdit || !selectedId) {
      setIsPointCapActive(false);
    }
  }, [canEdit, selectedId]);

  const getCenterFromMapRef = () => {
    const getCenter = map?.getCenter();
    const lat = getCenter?.lat();
    const lng = getCenter?.lng();
    return lat && lng ? { lat, lng } : mapCenter;
  };

  const options = {
    minZoom: 6,
    fullscreenControl: false,
    streetViewControl: false,
    mapTypeControl: false,
    zoomControl: false,
  };

  const handlePointCapChange = useCallback(
    (active: boolean) => {
      setIsPointCapActive(canEdit ? active : false);
    },
    [canEdit]
  );

  const wrapperClassName = [classes.mapWrapper, isPointCapActive ? classes.hideCreatePoint : '']
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {isLoaded && (
        <div className={wrapperClassName}>
          <GoogleMap
            center={getCenterFromMapRef()}
            mapContainerStyle={{ height: '100%' }}
            onLoad={onMapLoad}
            options={canEdit ? options : { ...options, gestureHandling: 'none' }}
            onUnmount={onMapUnmount}
            zoom={isSmallScreen ? 11 : 12}
          >
            <Marker position={mapCenter} />

            {zoneIds.map((id: number, idx) => {
              const mergedZone = displayZones[idx];
              if (!mergedZone || mergedZone.Id !== id) {
                return null;
              }
              return (
                <MapItem
                  key={id}
                  mergedZone={mergedZone}
                  index={idx}
                  selectedId={selectedId}
                  onSelect={onSelect}
                  onChange={onChange}
                  disabled={!canEdit}
                  onPointCapChange={handlePointCapChange}
                />
              );
            })}
          </GoogleMap>
        </div>
      )}
    </>
  );
};

type MappedState = ReturnType<ReturnType<typeof mapStateToPropsFactory>>;
const mapStateToPropsFactory = (initialState, ownProps) => {
  const loadingSelector = createLoadingSelector([deliveryZonesConstants.DELIVERY_ZONE_CREATE]);
  const zoneIdsSelector = createDisplayDeliveryZoneIdsSelector(ownProps.storeId);
  const displayZonesSelector = createDisplayDeliveryZonesSelector(ownProps.storeId);

  return (state: AppState) => {
    const canEdit = state.permissions.some(
      (p) => p === App.AppResourceSetEnum.UpdateDeliveryZones.toString()
    );

    return {
      createEventFired: loadingSelector(state),
      zoneIds: zoneIdsSelector(state),
      displayZones: displayZonesSelector(state),
      canEdit,
      selectedId: state.deliveryZones.selectedId,
    };
  };
};

export default connect(mapStateToPropsFactory)(GMap);
